import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import api from '../api';

// Finance uploads the per-campus fee workbook. The sheet is parsed in the
// browser (xlsx is already bundled), previewed for confirmation, then saved as
// a dated snapshot. Re-uploading the same campus + date replaces that snapshot.
const money = v => '₹ ' + Number(v || 0).toLocaleString('en-IN');
const today = () => new Date().toISOString().slice(0, 10);

export default function FeeUpload() {
  const [step, setStep] = useState('pick');       // pick → preview → done
  const [rows, setRows] = useState(null);
  const [fileName, setFileName] = useState('');
  const [pv, setPv] = useState(null);
  const [campuses, setCampuses] = useState([]);
  const [campusId, setCampusId] = useState('');
  const [asOf, setAsOf] = useState(today());
  const [visHeads, setVisHeads] = useState([]);   // fee heads to show on the dashboard
  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState('');     // existing board, or '' to create
  const [boardName, setBoardName] = useState(''); // name used when creating
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get('/fees/uploads').then(r => setHistory(r.data)).catch(() => {});
  const loadBoards = () => api.get('/fees/boards').then(r => setBoards(r.data)).catch(() => {});
  useEffect(() => {
    api.get('/master/campuses').then(r => setCampuses(r.data.filter(c => c.active))).catch(() => {});
    loadBoards(); loadHistory();
  }, []);

  const handleFile = e => {
    const file = e.target.files[0];
    if (!file) return;
    setErr(''); setBusy(true); setFileName(file.name);
    const reader = new FileReader();
    reader.onload = async evt => {
      try {
        const wb = XLSX.read(new Uint8Array(evt.target.result), { type: 'array' });
        // The detail sheet is the one that isn't the Dashboard tab
        const name = wb.SheetNames.find(n => !/dashboard/i.test(n)) || wb.SheetNames[0];
        const data = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: '', raw: false });
        setRows(data);
        const r = await api.post('/fees/preview', { rows: data });
        setPv(r.data);
        setCampusId(r.data.campus_match ? String(r.data.campus_match.id) : '');
        setBoardId(r.data.board_match ? String(r.data.board_match.id) : '');
        setBoardName(r.data.board_match ? r.data.board_match.name : (r.data.campus_label || ''));
        // Default: show every head except catch-alls like "Other"
        setVisHeads((r.data.heads || []).filter(h => !/^other\b|\bother\b/i.test(h)));
        setStep('preview');
      } catch (e) {
        setErr(e.response?.data?.error || 'Could not read that file. Upload the fee report .xlsx.');
      } finally { setBusy(false); }
    };
    reader.readAsArrayBuffer(file);
  };

  const save = async () => {
    if (!campusId) { setErr('Confirm the campus'); return; }
    setBusy(true); setErr('');
    try {
      const r = await api.post('/fees/upload', { rows, campus_id: Number(campusId), as_of_date: asOf,
        file_name: fileName, visible_heads: visHeads,
        board_id: boardId || null, board_name: boardName });
      setResult(r.data); setStep('done'); loadHistory(); loadBoards();
    } catch (e) { setErr(e.response?.data?.error || 'Upload failed'); }
    finally { setBusy(false); }
  };

  const reset = () => { setStep('pick'); setRows(null); setPv(null); setResult(null); setErr(''); setFileName(''); };

  const delSnap = async id => {
    if (!window.confirm('Delete this snapshot? Student records are not affected.')) return;
    try { await api.delete(`/fees/uploads/${id}`); loadHistory(); } catch (e) {}
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>💰 Fee Report Upload</h2>
        <p style={{ fontSize: 13, color: 'var(--text2)' }}>Upload the fee collection workbook for a campus. Each upload is saved with its date, so the dashboard can show any past report.</p>
      </div>

      {err && <div className="err-msg" style={{ marginBottom: 12 }}>{err}</div>}

      {step === 'pick' && (
        <div className="card" style={{ marginBottom: 18 }}>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginTop: 0 }}>
            Choose the <b>.xlsx</b> exported from the fee system. The detail sheet is read automatically — the Dashboard tab is ignored because the portal calculates those figures itself.
          </p>
          <label className="btn btn-primary" style={{ cursor: 'pointer', display: 'inline-block' }}>
            {busy ? 'Reading…' : 'Choose file…'}
            <input type="file" accept=".xlsx,.xls" onChange={handleFile} style={{ display: 'none' }} />
          </label>
        </div>
      )}

      {step === 'preview' && pv && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 12 }}>CHECK BEFORE SAVING — {fileName}</div>

          <div className="form-row">
            <div className="form-group">
              <label>Campus {pv.campus_match && <span style={{ fontSize: 11, color: 'var(--green)' }}>· detected “{pv.campus_label}”</span>}</label>
              <select value={campusId} onChange={e => setCampusId(e.target.value)}>
                <option value="">— select campus —</option>
                {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {pv.campus_match && pv.campus_match.confidence < 100 &&
                <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>Best match — please confirm it's right.</div>}
            </div>
            <div className="form-group">
              <label>Board {pv.board_match && <span style={{ fontSize: 11, color: 'var(--green)' }}>· matched</span>}</label>
              <select value={boardId} onChange={e => {
                  setBoardId(e.target.value);
                  const b = boards.find(x => String(x.id) === e.target.value);
                  if (b) { setBoardName(b.name); setCampusId(String(b.campus_id)); }
                }}>
                <option value="">+ New board: “{boardName || pv.campus_label}”</option>
                {boards.map(b => <option key={b.id} value={b.id}>{b.name} — {b.campus_name}</option>)}
              </select>
              {!boardId && (
                <input value={boardName} onChange={e => setBoardName(e.target.value)}
                  placeholder="Board name e.g. Madurai CBSE" style={{ marginTop: 4 }} />
              )}
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>
                Finance boards sit under a campus, so Madurai CBSE and Madurai SW stay separate but roll up to Madurai.
              </div>
            </div>
            <div className="form-group">
              <label>Report date</label>
              <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} />
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>This is the date you'll pick on the dashboard.</div>
            </div>
            <div className="form-group">
              <label>Installment</label>
              <input value={pv.installment_label} readOnly />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, margin: '10px 0 14px' }}>
            {[['Students', pv.students_total], ['Fully paid', pv.students_paid], ['With pending', pv.students_pending],
              ['Total', money(pv.totals.total)], ['Paid', money(pv.totals.paid)], ['Pending', money(pv.totals.pending)]].map(([l, v]) => (
              <div key={l} style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '10px 12px' }}>
                <div style={{ fontSize: 11, color: 'var(--text2)' }}>{l}</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{v}</div>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 12 }}>
            <div className="flex-gap" style={{ justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>
                WHICH FEE HEADS SHOULD THE DASHBOARD SHOW?
              </label>
              <div className="flex-gap" style={{ gap: 6 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setVisHeads(pv.heads)}>All</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setVisHeads([])}>None</button>
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 10, marginTop: 6 }}>
              {pv.heads.map(h => (
                <label className="opt" key={h} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, cursor: 'pointer' }}>
                  <input type="checkbox" checked={visHeads.includes(h)}
                    onChange={() => setVisHeads(v => v.includes(h) ? v.filter(x => x !== h) : [...v, h])} />
                  {h}
                </label>
              ))}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>
              Unticked heads are still saved and still counted in the totals — they're just left out of the dashboard's Category table.
            </div>
          </div>
          <div style={{ fontSize: 13, marginBottom: 12 }}>
            Students: <b>{pv.students_matched}</b> already in the directory · <b style={{ color: 'var(--accent)' }}>{pv.students_new}</b> will be added.
            {pv.students_new > 0 && <span style={{ color: 'var(--text2)' }}> New students are created with their name, grade &amp; section and parent contacts. Existing details are never overwritten.</span>}
          </div>

          <div className="flex-gap" style={{ gap: 8 }}>
            <button className="btn btn-secondary" onClick={reset}>Cancel</button>
            <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save this report'}</button>
          </div>
        </div>
      )}

      {step === 'done' && result && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--green)', marginBottom: 6 }}>✓ Report saved</div>
          <div style={{ fontSize: 13.5 }}>
            {result.students_total} students · {money(result.totals.paid)} collected of {money(result.totals.total)}
            {result.students_created > 0 && <> · <b>{result.students_created}</b> new students added to the directory</>}
            {result.replaced && <> · replaced the earlier report for this campus and date</>}
          </div>
          <div className="flex-gap" style={{ gap: 8, marginTop: 12 }}>
            <button className="btn btn-secondary" onClick={reset}>Upload another</button>
          </div>
        </div>
      )}

      <ManageBoards boards={boards} campuses={campuses} onChange={() => { loadBoards(); loadHistory(); }} />

      <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 18 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', padding: '10px 12px' }}>PREVIOUS UPLOADS</div>
        {!history.length ? <div className="empty-state"><p>No reports uploaded yet.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Date</th><th>Campus</th><th>Installment</th><th>Students</th><th>Total</th><th>Paid</th><th>Pending</th><th>By</th><th></th></tr></thead>
            <tbody>
              {history.map(h => (
                <tr key={h.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{h.as_of_date}</td>
                  <td>{h.campus_name}</td>
                  <td style={{ fontSize: 12 }}>{h.installment_label}</td>
                  <td>{h.students_total}</td>
                  <td>{money(h.total_amount)}</td>
                  <td style={{ color: 'var(--green)' }}>{money(h.paid_amount)}</td>
                  <td style={{ color: Number(h.pending_amount) > 0 ? 'var(--red)' : 'var(--text2)' }}>{money(h.pending_amount)}</td>
                  <td style={{ fontSize: 12, color: 'var(--text2)' }}>{h.uploaded_by_name || '—'}</td>
                  <td><button className="btn btn-danger btn-sm" onClick={() => delSnap(h.id)}>Delete</button></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

// Rename a board, move it to another campus, or merge two boards that were
// created from slightly different spellings of the same name.
function ManageBoards({ boards, campuses, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [edit, setEdit] = React.useState(null);      // { id, name, campus_id }
  const [mergeFrom, setMergeFrom] = React.useState(null);
  const [mergeInto, setMergeInto] = React.useState('');
  const [msg, setMsg] = React.useState('');
  const flash = m => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  const save = async () => {
    try {
      await api.put(`/fees/boards/${edit.id}`, { name: edit.name, campus_id: edit.campus_id });
      setEdit(null); flash('✓ Board updated'); onChange();
    } catch (e) { flash(e.response?.data?.error || 'Could not update the board'); }
  };
  const doMerge = async () => {
    if (!mergeInto) { flash('Pick the board to merge into'); return; }
    const target = boards.find(b => String(b.id) === String(mergeInto));
    if (!window.confirm(`Merge “${mergeFrom.name}” into “${target.name}”?\n\nIts reports move across and “${mergeFrom.name}” is removed. Where both hold a report for the same date and installment, the more recent upload is kept.`)) return;
    try {
      const r = await api.post(`/fees/boards/${mergeFrom.id}/merge`, { into_board_id: Number(mergeInto) });
      setMergeFrom(null); setMergeInto('');
      flash(`✓ Merged into ${r.data.into} — ${r.data.moved} report(s) moved${r.data.dropped ? `, ${r.data.dropped} older duplicate(s) dropped` : ''}`);
      onChange();
    } catch (e) { flash(e.response?.data?.error || 'Merge failed'); }
  };
  const del = async b => {
    if (!window.confirm(`Delete the board “${b.name}”?`)) return;
    try { await api.delete(`/fees/boards/${b.id}`); flash('✓ Board deleted'); onChange(); }
    catch (e) { flash(e.response?.data?.error || 'Could not delete'); }
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 18 }}>
      <div className="flex-gap" style={{ justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>
          MANAGE BOARDS <span style={{ fontWeight: 400 }}>— rename, move to another campus, or merge duplicates ({boards.length})</span>
        </span>
        <span style={{ color: 'var(--accent)', fontSize: 12 }}>{open ? 'Hide' : 'Show'}</span>
      </div>

      {open && (
        <div style={{ padding: '0 12px 12px' }}>
          {msg && <div style={{ fontSize: 13, marginBottom: 8, color: msg.startsWith('✓') ? 'var(--green)' : 'var(--red)' }}>{msg}</div>}
          {!boards.length ? <div style={{ fontSize: 13, color: 'var(--text2)' }}>No boards yet — they're created on your first upload.</div> : (
            <div className="table-wrap"><table>
              <thead><tr><th>Board</th><th>Campus</th><th style={{ width: 220 }}>Actions</th></tr></thead>
              <tbody>
                {boards.map(b => (
                  <tr key={b.id}>
                    <td>
                      {edit && edit.id === b.id
                        ? <input value={edit.name} onChange={e => setEdit(s => ({ ...s, name: e.target.value }))} style={{ width: '100%', padding: '4px 6px' }} />
                        : <span style={{ fontWeight: 500 }}>{b.name}</span>}
                    </td>
                    <td>
                      {edit && edit.id === b.id
                        ? <select value={edit.campus_id} onChange={e => setEdit(s => ({ ...s, campus_id: e.target.value }))} style={{ padding: '4px 6px' }}>
                            {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        : <span style={{ fontSize: 12, color: 'var(--text2)' }}>{b.campus_name}</span>}
                    </td>
                    <td>
                      {edit && edit.id === b.id ? (
                        <div className="flex-gap" style={{ gap: 6 }}>
                          <button className="btn btn-primary btn-sm" onClick={save}>Save</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => setEdit(null)}>Cancel</button>
                        </div>
                      ) : mergeFrom && mergeFrom.id === b.id ? (
                        <div className="flex-gap" style={{ gap: 6, flexWrap: 'wrap' }}>
                          <select value={mergeInto} onChange={e => setMergeInto(e.target.value)} style={{ padding: '4px 6px' }}>
                            <option value="">merge into…</option>
                            {boards.filter(x => x.id !== b.id).map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                          </select>
                          <button className="btn btn-primary btn-sm" onClick={doMerge}>Merge</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => { setMergeFrom(null); setMergeInto(''); }}>Cancel</button>
                        </div>
                      ) : (
                        <div className="flex-gap" style={{ gap: 6 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => setEdit({ id: b.id, name: b.name, campus_id: String(b.campus_id) })}>Rename</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => { setMergeFrom(b); setMergeInto(''); }}>Merge</button>
                          <button className="btn btn-danger btn-sm" onClick={() => del(b)}>Delete</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      )}
    </div>
  );
}
